import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { typedMemo } from '@utils';
import { DateTime } from 'luxon';
import { useCallback, useEffect, useMemo } from 'react';
import {
  Controller,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BaseControllerProps } from './types';
import { useFormFieldValidation } from './useFormFieldValidation';

export const DateFormats = {
  DATE_TIME_SECONDS: 'dd.MM.yyyy HH:mm:ss',
  DATE_TIME: 'dd.MM.yyyy HH:mm',
  TIME: 'HH:mm',
  DATE: 'dd.MM.yyyy',
  DAY_MONTH: 'dd-MM',
  DAY_MONTH_LONG: 'd. MMMM',
  MONTH_YEAR: 'yyyy-MM',
  MONTH_YEAR_LONG: 'MMMM yyyy',
  YEAR: 'yyyy',
  MONTH: 'MMMM',
} as const;

type DateFormat = (typeof DateFormats)[keyof typeof DateFormats];

interface DateRangeValidation<TFieldValues extends FieldValues = FieldValues> {
  fromDateField?: FieldPath<TFieldValues>;
  toDateField?: FieldPath<TFieldValues>;
}

interface DateFormFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends BaseControllerProps<TFieldValues, TName> {
  required?: boolean;
  label?: string;
  format?: DateFormat;
  dateRangeValidation?: DateRangeValidation<TFieldValues>;
}

export const DateFormField = typedMemo(
  <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  >({
    name,
    control,
    rules,
    required,
    label,
    format = DateFormats.DATE,
    dateRangeValidation,
  }: Readonly<DateFormFieldProps<TFieldValues, TName>>) => {
    const { t } = useTranslation();
    const { trigger } = useFormContext();

    const normalizeDateValue = useCallback(
      (val: DateTime | string | null | undefined): DateTime | null => {
        if (val instanceof DateTime && val.isValid) return val;
        if (typeof val === 'string') {
          const parsed = DateTime.fromISO(val);
          return parsed.isValid ? parsed : null;
        }
        return null;
      },
      [],
    );

    // Watch the related date fields for validation
    const fromDateValue = useWatch({
      control,
      name: dateRangeValidation?.fromDateField ?? ('' as TName),
    });

    const toDateValue = useWatch({
      control,
      name: dateRangeValidation?.toDateField ?? ('' as TName),
    });

    // Trigger revalidation when related field values change
    useEffect(() => {
      if (dateRangeValidation) {
        const { fromDateField, toDateField } = dateRangeValidation;
        if (name === fromDateField && toDateField) {
          trigger(fromDateField);
        } else if (name === toDateField && fromDateField) {
          trigger(toDateField);
        }
      }
    }, [fromDateValue, toDateValue, dateRangeValidation, name, trigger]);

    // Base validation rules
    const baseValidationRules = useFormFieldValidation<TFieldValues, TName>(
      required,
      rules,
    );

    // Create validation rules including date range validation - memoized
    const validationRules = useMemo(() => {
      if (!dateRangeValidation) return baseValidationRules;

      return {
        ...baseValidationRules,
        validate: {
          dateRange: (value: DateTime | null | string) => {
            const currentDate = normalizeDateValue(value);
            if (!currentDate) return true;

            const { fromDateField, toDateField } = dateRangeValidation;

            // Validate from date
            if (name === fromDateField && toDateValue && currentDate) {
              const toDate = normalizeDateValue(toDateValue);
              if (!toDate) return true;
              return (
                currentDate <= toDate ||
                t('validation.fromDateMustBeBeforeToDate')
              );
            }

            // Validate to date
            if (name === toDateField && fromDateValue && currentDate) {
              const fromDate = normalizeDateValue(fromDateValue);
              if (!fromDate) return true;
              return (
                currentDate >= fromDate ||
                t('validation.toDateMustBeAfterFromDate')
              );
            }

            return true;
          },
        },
      };
    }, [
      baseValidationRules,
      dateRangeValidation,
      name,
      toDateValue,
      fromDateValue,
      t,
      normalizeDateValue,
    ]);

    const shouldDisableDate = useCallback(
      (date: DateTime) => {
        if (!dateRangeValidation) {
          return false;
        }
        const { fromDateField, toDateField } = dateRangeValidation;

        if (name === fromDateField && toDateValue) {
          const toDate = normalizeDateValue(toDateValue);
          if (!toDate) return false;
          return date > toDate;
        }

        if (name === toDateField && fromDateValue) {
          const fromDate = normalizeDateValue(fromDateValue);
          if (!fromDate) return false;
          return date < fromDate;
        }
        return false;
      },
      [
        dateRangeValidation,
        name,
        toDateValue,
        fromDateValue,
        normalizeDateValue,
      ],
    );

    // Memoized render function for the Controller
    const controllerRender = useCallback(
      ({
        field,
        fieldState: { error },
      }: {
        field: ControllerRenderProps<TFieldValues, TName>;
        fieldState: { error?: { message?: string } };
      }) => {
        const rawValue = field.value as DateTime | null | undefined | string;
        const value = normalizeDateValue(rawValue);

        const handleChange = (newValue: DateTime | null) => {
          field.onChange(newValue);
        };

        const commonProps = {
          label,
          value,
          onChange: handleChange,
          format,
          shouldDisableDate,
          slotProps: {
            textField: {
              disabled: field.disabled,
              required: required,
              error: !!error,
              helperText: error?.message || ' ',
              name: field.name,
              onBlur: field.onBlur,
              size: 'small' as const,
              margin: 'dense' as const,
            },
          },
        };

        switch (format) {
          case DateFormats.DATE_TIME_SECONDS:
          case DateFormats.DATE_TIME:
            return <DateTimePicker {...commonProps} />;

          case DateFormats.TIME:
            return <TimePicker {...commonProps} />;

          case DateFormats.YEAR:
            return <DatePicker {...commonProps} views={['year']} />;

          case DateFormats.MONTH:
            return <DatePicker {...commonProps} views={['month']} />;

          case DateFormats.MONTH_YEAR:
          case DateFormats.MONTH_YEAR_LONG:
            return <DatePicker {...commonProps} views={['year', 'month']} />;

          case DateFormats.DAY_MONTH:
          case DateFormats.DAY_MONTH_LONG:
            return <DatePicker {...commonProps} views={['month', 'day']} />;

          default:
            return <DatePicker {...commonProps} />;
        }
      },
      [label, format, shouldDisableDate, required, normalizeDateValue],
    );

    return (
      <Controller
        name={name}
        control={control}
        rules={validationRules}
        render={controllerRender}
      />
    );
  },
);
